import { isProduction } from "../../apps/api/helpers/isProduction";

const BUN_PROXY_PORT = globalThis?.process?.env?.BUN_PROXY_PORT || "80";

export function getBaseUrl(): string {
  if (process) {
    return isProduction() ? process.env.BASE_URL || "" : `http://localhost:${BUN_PROXY_PORT}`;
  }

  return "https://worldofwow.com/";
}

export function buildUrl(path: string, params: Record<string, string>): string {
  const base = getBaseUrl();
  const url = new URL(path, base);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, value);
    }
  });

  return url.toString();
}

export type AppType = "" | "my" | "api";

export function buildAppUrl(app: AppType, path: string, params: Record<string, string> = {}): string {
  const base = getBaseUrl();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const fullPath = `/${app}${cleanPath}`;

  const url = new URL(fullPath, base);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, value);
    }
  });

  return url.toString();
}

/**
 * Allowed hostnames for OAuth redirect URLs.
 * Prevents open redirect attacks by only allowing redirects to trusted domains.
 */
const ALLOWED_REDIRECT_HOSTS = [
  "worldofwow.com",
  "www.worldofwow.com",
  "worldofwow.dev",
  "www.worldofwow.dev",
  "localhost",
  "127.0.0.1",
];

/**
 * Validates that a redirect URL is safe to use.
 * Allows:
 * - Relative URLs (starting with /)
 * - Same-origin URLs (matching allowed hosts)
 *
 * Blocks:
 * - External domains (potential phishing)
 * - javascript: URLs
 * - data: URLs
 * - Protocol-relative URLs (//evil.com)
 */
export function isValidRedirectUrl(url: string | undefined | null): boolean {
  if (!url) return false;

  // Block protocol-relative URLs (//evil.com)
  if (url.startsWith("//")) {
    return false;
  }

  // Allow relative URLs starting with /
  if (url.startsWith("/") && !url.startsWith("//")) {
    return true;
  }

  try {
    const parsedUrl = new URL(url);

    // Block non-http(s) protocols (javascript:, data:, etc.)
    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
      return false;
    }

    // Check if hostname is in allowlist
    const hostname = parsedUrl.hostname.toLowerCase();
    return ALLOWED_REDIRECT_HOSTS.some((allowed) => hostname === allowed || hostname.endsWith(`.${allowed}`));
  } catch {
    // Invalid URL format
    return false;
  }
}

/**
 * Sanitizes a redirect URL, returning a safe default if invalid.
 * Use this when you need a guaranteed safe URL.
 */
export function sanitizeRedirectUrl(url: string | undefined | null, fallback = "/"): string {
  return isValidRedirectUrl(url) ? url! : fallback;
}

export function isMailtoUrl(url: string): boolean {
  return url.trim().toLowerCase().startsWith("mailto:");
}

export function validateUrl(url: string): boolean {
  try {
    if (isMailtoUrl(url)) {
      const emailPart = url.slice(7).split("?")[0]!;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailPart);
    }

    const urlObject = new URL(url);

    // The protocol check is implicitly handled by the URL constructor now,
    // but we can keep it for clarity if we want to be explicit.
    if (urlObject.protocol !== "http:" && urlObject.protocol !== "https:") {
      return false;
    }

    // Check for localhost or private IP addresses
    const hostname = urlObject.hostname;
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.startsWith("192.168.") ||
      hostname.startsWith("10.") ||
      hostname.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./)
    ) {
      return false;
    }

    // A simple check to ensure the hostname contains a dot, which is typical for public domains.
    if (!hostname.includes(".")) {
      return false;
    }

    return true;
  } catch {
    // If the URL constructor throws an error, it's not a valid URL
    return false;
  }
}
